import React, { useState } from "react";
import get from "lodash/fp/get";
import getOr from "lodash/fp/getOr";
import AsyncCollapse from "~/src/components/async-collapse";
import ViewEditShift from "~/src/containers/modals/view-edit-shift";
import ViewEditBooking from "~/src/containers/modals/view-edit-booking";
import { connect } from "react-redux";
import updateBooking from "~/src/graphql/mutations/update-booking/update-booking.decorator";
import FETCH_MEMBER_BOOKINGS from "~/src/graphql/queries/get-member-bookings/get-member-bookings.query";
import FETCH_MEMBER_TIMESHEETS_COUNT from "~/src/graphql/queries/get-member-timesheets/get-member-timesheets-count.query";
import { useQuery } from "@apollo/client";
import useAuth from "~/src/auth/hooks/use-auth";
import useModalState from "~/src/hooks/useModalState";
import useBookingActions from "~/src/hooks/useBookingActions";
import { BOOKING_STATES } from "~/src/consts";
import { BOOKING_COUNT_KEYS } from "~/src/constants/bookingConstants";

import tabStructure from "./tab-structure";
import { setTabIndex } from "../../reducer";
import TimesheetsTab from "./timesheets-tab";
import TabNavigation from "./TabNavigation";
import BookingList from "./BookingList";
import { ListContainer } from "./member-shift-list.styles";

const getOrderByDirection = memberQueryData => {
  const bookingState = memberQueryData.bookingState || "";
  const bookingStates = memberQueryData.bookingStates || [];

  const shouldDescend =
    (BOOKING_STATES[bookingState] === BOOKING_STATES.ACCEPTED ||
      bookingStates.includes(BOOKING_STATES.ACCEPTED)) &&
    memberQueryData.startBefore;

  if (shouldDescend) {
    return "DESC";
  }

  return "ASC";
};

const MemberShiftList = props => {
  const auth = useAuth();
  const {
    selectedBookingId,
    selectedShiftId,
    sourceAccountId,
    handleOpenBookingModal,
    handleCloseBookingModal,
    handleOpenShiftModal,
    handleCloseShiftModal
  } = useModalState();

  const { handleStateChange, handleLoadMore } = useBookingActions(
    props.updateBooking
  );

  const memberQueryData = props.memberBookingList[props.memberId] || {};

  const activeTab = getOr(
    "",
    `memberBookingList.${props.memberId}.activeTab`,
    props
  );
  const currentTabConfig = tabStructure.find(tab => tab.keyProp === activeTab);
  const isTimesheetTab = currentTabConfig?.isTimesheetTab || false;

  const {
    data: bookingData,
    loading: bookingLoading,
    fetchMore: fetchMoreBookings
  } = useQuery(FETCH_MEMBER_BOOKINGS, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    skip: false,
    variables: auth.addVals(FETCH_MEMBER_BOOKINGS, {
      limit: 10,
      offset: 0,
      memberId: props.memberId,
      stateEquals: get("bookingState", memberQueryData)
        ? BOOKING_STATES[memberQueryData.bookingState]
        : undefined,
      stateIn: memberQueryData.bookingStates || undefined,
      startBefore: memberQueryData.startBefore || undefined,
      startAfter: memberQueryData.startAfter || undefined,
      isWithAccount: true,
      orderByColumn: "shifts.startTime",
      orderByDirection: getOrderByDirection(memberQueryData)
    })
  });

  // State for timesheet count
  const [timesheetCount, setTimesheetCount] = useState(0);

  useQuery(FETCH_MEMBER_TIMESHEETS_COUNT, {
    fetchPolicy: "cache-first",
    variables: auth.addVals(FETCH_MEMBER_TIMESHEETS_COUNT, {
      memberId: props.memberId
    }),
    onCompleted: data => {
      const count = data?.memberTimesheetsCount?.totalCount || 0;
      setTimesheetCount(count);
    }
  });

  const loading = !isTimesheetTab && bookingLoading;
  const items = bookingData?.account?.member?.bookings?.data || [];

  const bookingCounts = bookingData?.account?.member?.bookingsCount || {};
  const itemsCount = {
    ...bookingCounts,
    timesheetsCount: timesheetCount
  };

  const getTotalBookings = () => {
    if (bookingCounts) {
      return BOOKING_COUNT_KEYS.reduce(
        (sum, key) => sum + (bookingCounts[key] || 0),
        0
      );
    }
    return 0;
  };

  const loadMore = () => handleLoadMore(fetchMoreBookings, items);

  const shouldDisableLoadMore = () => {
    if (!items || !items.length || !activeTab) {
      return true;
    }

    // Only enable load more if there is more to load 🙈
    if (itemsCount[activeTab] <= items.length) {
      return true;
    }

    return false;
  };

  const totalBookings = getTotalBookings();

  return (
    <AsyncCollapse isExpanded={props.isExpanded} isLoading={loading}>
      <ViewEditShift
        isOpen={!!selectedShiftId}
        onClose={handleCloseShiftModal}
        shiftId={selectedShiftId}
      />
      <ViewEditBooking
        isOpen={!!selectedBookingId}
        onClose={handleCloseBookingModal}
        bookingId={selectedBookingId}
        sourceAccountId={sourceAccountId}
      />
      <ListContainer>
        <TabNavigation
          activeTab={activeTab}
          itemsCount={{ ...bookingCounts, timesheetsCount: timesheetCount }}
          totalBookings={totalBookings}
          memberId={props.memberId}
          onTabChange={tabData => props.dispatch(setTabIndex(tabData))}
        />
        {isTimesheetTab ? (
          <TimesheetsTab
            memberId={props.memberId}
            memberQueryData={memberQueryData}
            itemsCount={itemsCount}
            onTimesheetCountUpdate={setTimesheetCount}
            onOpenBookingModal={handleOpenBookingModal}
            onOpenShiftModal={handleOpenShiftModal}
          />
        ) : (
          <BookingList
            items={items}
            activeTab={activeTab}
            isTimesheetTab={isTimesheetTab}
            shouldDisableLoadMore={shouldDisableLoadMore()}
            onStateChange={handleStateChange}
            onOpenBookingModal={handleOpenBookingModal}
            onOpenShiftModal={handleOpenShiftModal}
            onLoadMore={loadMore}
          />
        )}
      </ListContainer>
    </AsyncCollapse>
  );
};

const mapStateToProps = s => ({
  memberBookingList: s.myStaff.memberBookingList
});

export default connect(mapStateToProps)(updateBooking(MemberShiftList));
